import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Avatar, Dialog, IconButton, Tooltip } from '@mui/material';
import HighlightOffOutlinedIcon from '@mui/icons-material/HighlightOffOutlined';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonIcon from '@mui/icons-material/Person';
import {
  startManualShiftAPICall,
  endManualShiftAPICall,
} from '../../../API/Settings/Shift/manualShiftAPI';
import { verifyPasswordForShiftAPICall } from '../../../API/Login/VerifyPasswordAPI';
import {
  getShiftEndDetail,
  getCheckInDetail,
  getCheckOutDetail,
  getCheckOutSubmit,
  getVerifypassword,
} from '../../../component/Modules/Sales/Billing/newBillingAPI';
import { ListPosCheckinSummaryAPI } from '../../../component/Modules/PointOfSale/Report/ReportAPI';
import { getShiftStatusAPICAll } from '../../../API/Settings/Shift/viewShiftAPI';
import { getCurrentTime, today } from '../../../Js/Date';
import ErrorSnackbar from '../../../component/Single Components/SnackBars/ErrorSnackbar';
import SuccessSnackbar from '../../../component/Single Components/SnackBars/SuccessSnackbar';
import photo from '../../../Assets/Images/b1fac8457e015cf2d2dcf2fbebe8c9f2.jpg';

// resolve an employee display name from various shapes
const resolveEmpName = (emp, fallback) =>
  emp?.staff_name || emp?.name || emp?.empName || fallback || 'Employee';

// Self-contained copy of the desktop shift Start/End flow (same APIs + markup)
// so the self-order 3-bar menu can start/end shifts exactly like the POS desktop.
const ShiftControls = () => {
  // Redux state (same selectors as desktop Dashboard)
  const userInfo = useSelector((state) => state.loginResponseSlice.value);
  const shiftStatus = useSelector((state) => state.shiftStatusSlice.value);
  const allEmployees = useSelector((state) => state.allActiveEmployeeSlice.value);
  const { checkOutPassDetail, checkOutDetail, shiftEndtDetail, checkInDetail, checkInPass } =
    useSelector((state) => state.newBillingSlice);
  const checkinSummary = useSelector((state) => state.posReportshiftSlice.shiftReporCheckinSummary);

  // localStorage values (same as desktop)
  const storeCode = localStorage.getItem('branchId');
  const userLoginMeta = localStorage.getItem('login_meta');
  const userBranchpk = JSON.parse(userLoginMeta || '{}')?.branchPk;
  const employeeId = JSON.parse(userLoginMeta || '{}')?.data?._id;
  const userShiftCode = JSON.parse(localStorage.getItem('shiftStatus') || '{}')?._id;

  const date = new Date();
  const isShiftActive = Boolean(shiftStatus);

  // local states (mirror desktop)
  const [shiftBtnState, setShiftBtnState] = useState(false);
  const [shiftEndPasswordDialog, setShiftEndPasswordDialog] = useState(false);
  const [denominationDialog, setDenominationDialog] = useState(false);
  const [verifyPassWord, setVerifyPassword] = useState('');
  const [errorSnackBar, setErrorSnackBar] = useState(false);
  const [sucessSnackBar, setSuccessSnackBar] = useState(false);
  const [errorMsg, setErrorMsg] = useState('Error!!');
  const [showUpArrow, setShowUpArrow] = useState(false);
  const [denomination, setDenomination] = useState([]);
  const [description, setDescription] = useState('');
  const [entries, setEntries] = useState([{ currency: '', count: '' }]);

  // checkout flow states (mirror desktop)
  const [showCheckOutPopup, setShowCheckOutPopup] = useState(false);
  const [showSecondCheckOutPopup, setShowSecondCheckOutPopup] = useState(false);
  const [showFinalCheckOutPopup, setShowFinalCheckOutPopup] = useState(false);
  const [selectedCheckoutEmployee, setSelectedCheckoutEmployee] = useState(null);
  const [password, setPassword] = useState('');
  const [checkoutInitiated, setCheckoutInitiated] = useState(false);

  // check-in flow states (re-check-in after a checkout, while shift is still active)
  const [showCheckinPopup, setShowCheckinPopup] = useState(false);
  const [checkinPassword, setCheckinPassword] = useState('');
  const [checkinInitiated, setCheckinInitiated] = useState(false);

  // current user is checked-out (shift still active) -> allow re-check-in.
  // At shift start check-in is automatic, so checkIn is true and this stays false.
  const isCurrentUserCheckedOut =
    checkInDetail?.data?.checkIn === false || checkInDetail?.data?.checkIn === 'false';

  // checked-in staff list (same source as desktop CheckInStatusIndicator)
  const activeCheckins = (checkinSummary?.empCheckIn || []).filter((c) => {
    const hasEnded =
      c?.checkOut === true || c?.checkOut === 'true' || !!c?.checkOutDate || !!c?.checkOutTime;
    return !hasEnded;
  });
  const checkinName = (c) => {
    const fresh = Array.isArray(allEmployees)
      ? allEmployees.find((e) => e?._id === (c?.empData?._id || c?.empId))
      : null;
    return resolveEmpName(fresh || c?.empData, c?.empId?.slice(-4));
  };

  // timer handlers required by API signatures (no-op here; ShiftTimerComponent handles UI)
  const handleStart = () => {};
  const handlePause = () => {};
  const handleReset = () => {};

  // shift start
  const shiftStartBtnFn = () => {
    startManualShiftAPICall(
      {
        branchId: storeCode,
        startDate: `${today} ${getCurrentTime()}`,
      },
      setShiftBtnState,
      shiftBtnState,
      handleReset,
      handleStart,
      setErrorSnackBar,
      setErrorMsg
    );
  };

  // shift end - guard for checked-in employees, then branch-wise path
  const shiftEndBtnClick = () => {
    const activeCheckedIn = Array.isArray(shiftStatus?.empCheckIn)
      ? shiftStatus.empCheckIn.filter((e) => e?.checkInStatus)
      : [];
    if (activeCheckedIn.length > 0) {
      const empMapList = Array.isArray(allEmployees) ? allEmployees : [];
      const names = activeCheckedIn
        .map((e) => {
          const emp = empMapList.find((x) => x?._id === e.empId);
          return emp?.staff_name || e.empId?.slice(-4) || 'Unknown';
        })
        .join(', ');
      setErrorMsg(`Please checkout the following before ending shift: ${names}`);
      setErrorSnackBar(true);
      return;
    }
    if (shiftStatus?.isBranchWise === false) {
      getShiftEndDetail({ shiftId: userShiftCode });
    } else {
      setShiftEndPasswordDialog(true);
    }
  };

  // open denomination dialog once shift end detail is fetched
  useEffect(() => {
    if (shiftEndtDetail?.success === true) {
      setDenominationDialog(true);
    }
  }, [shiftEndtDetail]);

  // denomination totals / difference (same as desktop)
  const totalDenomination = denomination.reduce((sum, entry) => {
    const currency = parseFloat(entry.currency) || 0;
    const count = parseInt(entry.count) || 0;
    return sum + currency * count;
  }, 0);
  const cashTypeTotal = checkOutPassDetail?.data?.cashCardSummary?.data
    ?.filter((item) => item.type === 'cash')
    ?.reduce((sum, item) => sum + (parseFloat(item.totalAmount) || 0), 0);
  const difference = cashTypeTotal - totalDenomination;

  // denomination entry handlers (same as desktop)
  const handleChange = (index, field, value) => {
    const updated = [...entries];
    updated[index][field] = value;
    setEntries(updated);
  };
  const addEntry = () => {
    const validEntries = entries.filter((e) => e.currency && e.count);
    const denominationTotal = validEntries.reduce((sum, entry) => {
      const currency = parseFloat(entry.currency) || 0;
      const count = parseInt(entry.count) || 0;
      return sum + currency * count;
    }, 0);
    const cashTotal = checkOutPassDetail?.data?.cashCardSummary?.data
      ?.filter((item) => item.type === 'cash')
      ?.reduce((sum, item) => sum + (parseFloat(item.totalAmount) || 0), 0);
    if (denominationTotal > cashTotal) {
      alert('Denomination total cannot exceed the expected cash total.');
      return;
    }
    setEntries([...entries, { currency: '', count: '' }]);
  };
  const deleteEntry = (index) => {
    setEntries((prev) => {
      const updated = [...prev];
      updated.splice(index, 1);
      return updated.length ? updated : [{ currency: '', count: '' }];
    });
  };
  const toggleUpArrow = () => {
    setShowUpArrow((prev) => {
      const open = !prev;
      if (open) {
        setEntries(denomination.length > 0 ? denomination : [{ currency: '', count: '' }]);
      }
      return open;
    });
  };
  const handleAddSubmit = () => {
    const validEntries = entries.filter((e) => e.currency && e.count);
    const currencySet = new Set();
    const hasDuplicate = validEntries.some((entry) => {
      if (currencySet.has(entry.currency)) return true;
      currencySet.add(entry.currency);
      return false;
    });
    if (hasDuplicate) {
      alert('Duplicate currency values are not allowed.');
      return;
    }
    const cashAmount = (shiftStatus?.isBranchWise === true
      ? checkOutPassDetail?.data?.cashCardSummary?.data
      : shiftEndtDetail?.data?.cashCardSummary?.data
    )?.find((r) => r.type === 'cash')?.totalAmount || 0;
    const total = validEntries.reduce((sum, entry) => {
      const currency = parseFloat(entry.currency) || 0;
      const count = parseInt(entry.count) || 0;
      return sum + currency * count;
    }, 0);
    if (total > cashAmount) {
      alert('Total denomination exceeds available cash.');
      return;
    }
    setDenomination(validEntries);
    setEntries([{ currency: '', count: '' }]);
    setShowUpArrow(false);
  };

  // end shift API call after password verification
  const shiftEndPasswordSubmitBtnClick = () => {
    endManualShiftAPICall(
      {
        branchId: userBranchpk,
        endDate: `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
          date.getDate()
        ).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(
          date.getMinutes()
        ).padStart(2, '0')}:${String(date.getSeconds()).padStart(2, '0')}`,
      },
      handlePause,
      setShiftEndPasswordDialog,
      setShiftBtnState,
      shiftBtnState,
      setErrorSnackBar,
      setErrorMsg
    );
    localStorage.setItem('shitBtnState', JSON.stringify(!shiftBtnState));
    localStorage.removeItem('timer');
  };

  // verify password (same body logic as desktop)
  const verifyUserPassword = () => {
    if (verifyPassWord !== '') {
      let body;
      if (shiftStatus?.isBranchWise === true) {
        body = { password: verifyPassWord };
      } else {
        body = {
          password: verifyPassWord,
          endDate: `${today} ${getCurrentTime()}`,
          branchId: userBranchpk,
          empWiseCashCardReport: shiftEndtDetail?.data?.cashCardSummary?.data?.map((item) => {
            const base = {
              name: item.name,
              type: item.type,
              journalId: item.journalId,
              totalAmount: item.totalAmount,
              totalCommission: item.totalCommission,
            };
            if (item.type === 'cash') {
              return {
                ...base,
                differance: difference,
                denomination: denomination,
                description: description,
              };
            }
            return base;
          }),
        };
      }
      verifyPasswordForShiftAPICall(body, setErrorSnackBar, setErrorMsg, shiftEndPasswordSubmitBtnClick);
    }
  };

  const errorSnackbarClose = () => setErrorSnackBar(false);
  const successSnackbarClose = () => setSuccessSnackBar(false);

  // ---- check-in / checkout flow (same APIs as desktop) ----

  // load checked-in staff for the active shift
  useEffect(() => {
    if (isShiftActive && userShiftCode) {
      // getVerifypassword reads userShiftpk from localStorage internally
      localStorage.setItem('userShiftpk', userShiftCode);
      ListPosCheckinSummaryAPI({ shiftId: userShiftCode });
      getCheckInDetail({ shiftId: userShiftCode });
    }
  }, [isShiftActive, userShiftCode]);

  const refreshShiftRelatedData = () => {
    if (userShiftCode) {
      getCheckInDetail({ shiftId: userShiftCode });
      ListPosCheckinSummaryAPI({ shiftId: userShiftCode });
    }
    if (storeCode) {
      getShiftStatusAPICAll({ branchId: storeCode, curTime: `${today} ${getCurrentTime()}` });
    }
  };

  const openCheckout = (emp) => {
    setSelectedCheckoutEmployee(emp || null);
    setCheckoutInitiated(true);
    setShowCheckOutPopup(true);
  };

  // verify checkout password -> fetches the checkout payment report
  const VerifyCheckOutPass = async (pwd) => {
    try {
      await getCheckOutDetail({ password: pwd, shiftId: userShiftCode });
    } catch (error) {
      if (error?.response?.status === 409) {
        setErrorMsg('Checkout already completed');
        setErrorSnackBar(true);
        refreshShiftRelatedData();
        setShowCheckOutPopup(false);
        setShowSecondCheckOutPopup(false);
        setShowFinalCheckOutPopup(false);
        return;
      }
      setErrorMsg('Checkout process failed');
      setErrorSnackBar(true);
    }
  };

  // open final checkout popup once payment report is fetched
  useEffect(() => {
    if (checkoutInitiated && checkOutPassDetail?.success === true) {
      setShowSecondCheckOutPopup(false);
      setShowFinalCheckOutPopup(true);
    }
  }, [checkOutPassDetail, checkoutInitiated]);

  // submit checkout (same body as desktop)
  const handleSubmitheckout = () => {
    const body = {
      postDate: `${today} ${getCurrentTime()}`,
      shiftId: userShiftCode,
      employeeName: userInfo?.userName,
      employeeId: employeeId,
      cashCardReport: checkOutPassDetail?.data?.cashCardSummary?.data?.map((item) => {
        const base = {
          type: item.type,
          journalId: item.journalId,
          totalAmount: item.totalAmount,
          name: item.name,
          totalCommission: item.totalCommission,
        };
        if (item.type === 'cash') {
          return { ...base, differance: difference, denomination: denomination, description: description };
        }
        return base;
      }),
    };
    getCheckOutSubmit(body, () => {
      refreshShiftRelatedData();
      setShowFinalCheckOutPopup(false);
      setPassword('');
      setDenomination([]);
      setDescription('');
      setShowUpArrow(false);
      setCheckoutInitiated(false);
    });
  };

  // close popups after a successful checkout
  const isCheckedOut =
    checkOutDetail?.data?.checkOut === true || checkOutDetail?.data?.checkOut === 'true';
  useEffect(() => {
    if (checkoutInitiated && isCheckedOut) {
      setShowFinalCheckOutPopup(false);
      setCheckoutInitiated(false);
    }
  }, [isCheckedOut, checkoutInitiated]);

  // ---- re-check-in flow (same getVerifypassword API as desktop) ----
  const openCheckin = () => {
    setCheckinPassword('');
    setCheckinInitiated(true);
    setShowCheckinPopup(true);
  };

  const verifyCheckinPass = (pwd) => {
    if (!pwd) return;
    getVerifypassword(
      { password: pwd, shiftId: userShiftCode, postDate: `${today} ${getCurrentTime()}` },
      setErrorSnackBar,
      setErrorMsg
    );
  };

  // close check-in popup + refresh once check-in succeeds
  useEffect(() => {
    if (checkinInitiated && checkInPass?.data?.checkIn === true) {
      setShowCheckinPopup(false);
      setCheckinPassword('');
      setCheckinInitiated(false);
      refreshShiftRelatedData();
    }
  }, [checkInPass, checkinInitiated]);

  return (
    <>
      <div className="justify-center" style={{ width: '100%' }}>
        {isShiftActive ? (
          <button
            style={{ height: '34px', minWidth: '140px' }}
            onClick={shiftEndBtnClick}
            className="start-button end-button"
          >
            Shift End
          </button>
        ) : (
          <button
            style={{ height: '34px', minWidth: '140px' }}
            onClick={shiftStartBtnFn}
            className="start-button"
          >
            Shift Start
          </button>
        )}
      </div>

      {/* re-check-in: appears only after the current user has checked out (shift still active).
          At shift start check-in is automatic, so this stays hidden. */}
      {isShiftActive && isCurrentUserCheckedOut && (
        <div className="justify-center" style={{ width: '100%', marginTop: '12px' }}>
          <button
            style={{ height: '34px', minWidth: '140px' }}
            onClick={openCheckin}
            className="start-button"
          >
            Check In
          </button>
        </div>
      )}

      {/* checked-in staff list (visible while shift is active) - checkout before ending shift */}
      {isShiftActive && (
        <div style={{ width: '100%', marginTop: '14px' }}>
          <p style={{ fontWeight: 600, margin: '0 0 6px 0', fontSize: '13px', color: '#333' }}>
            Checked-in staff
          </p>
          {activeCheckins.length === 0 ? (
            <div style={{ fontSize: '12px', color: '#888', padding: '6px 0' }}>
              No staff checked-in
            </div>
          ) : (
            activeCheckins.map((c) => {
              const emp = c?.empData || { _id: c?.empId };
              const name = checkinName(c);
              return (
                <div
                  key={c?._id || c?.empId}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '8px',
                    padding: '6px 8px',
                    margin: '6px 0',
                    border: '1px solid #e0e0e0',
                    borderRadius: '6px',
                    backgroundColor: '#f7f7f7',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Avatar sx={{ width: 26, height: 26, bgcolor: '#bdbdbd' }}>
                      <PersonIcon sx={{ fontSize: 18 }} />
                    </Avatar>
                    <span style={{ fontSize: '13px', fontWeight: 600 }}>{name}</span>
                  </div>
                  <Tooltip title="Checkout">
                    <IconButton size="small" color="error" onClick={() => openCheckout(emp)}>
                      <LogoutIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* password dialog */}
      <Dialog
        open={shiftEndPasswordDialog}
        onClose={() => setShiftEndPasswordDialog(false)}
        maxWidth="lg"
      >
        <div className="shift-end-button-password-container">
          <div className="justify-space-between">
            <h3>Enter Password</h3>
          </div>
          <div className="global-single-input shit-end-password-input">
            <p>Password</p>
            <input
              type="password"
              value={verifyPassWord}
              onKeyDown={(e) =>
                e.key === 'Enter'
                  ? verifyUserPassword()
                  : e.key === 'Escape'
                  ? setShiftEndPasswordDialog(false)
                  : undefined
              }
              onChange={(e) => setVerifyPassword(e.target.value)}
            />
          </div>
          <button onClick={verifyUserPassword} className="btn btn-primary shift-end-password-btn">
            Submit
          </button>
        </div>
      </Dialog>

      {/* error / success snackbars */}
      <ErrorSnackbar open={errorSnackBar} handleClose={errorSnackbarClose} message={errorMsg} />
      <SuccessSnackbar open={sucessSnackBar} handleClose={successSnackbarClose} message={errorMsg} />

      {/* SHIFT end denomination dialog */}
      {denominationDialog && (
        <div className="popup-overlay">
          <div className="popup-box" style={{ width: '387px', borderRadius: '10px', overflow: 'hidden' }}>
            {/* Top bar */}
            <div
              style={{
                backgroundColor: '#14008C',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '10px',
                color: 'white',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <img src={photo} alt="" style={{ borderRadius: '50%', height: '35px', width: '35px' }} />
                <span style={{ fontWeight: 'bold' }}>{userInfo?.userName}</span>
              </div>
              <IconButton onClick={() => setDenominationDialog(false)} style={{ color: 'white' }}>
                <HighlightOffOutlinedIcon />
              </IconButton>
            </div>
            {/* Content */}
            <div style={{ padding: '15px', backgroundColor: '#fff' }}>
              <h4 style={{ margin: '1px 0px 15px 0px', justifySelf: 'start', fontWeight: '400' }}>Denomination</h4>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  color: 'black',
                  marginBottom: '10px',
                  paddingInline: '9px',
                  borderRadius: '3px',
                  backgroundColor: 'rgb(163 160 160)',
                }}
              >
                <span>Total</span>
                <span>{shiftEndtDetail?.data?.cashCardSummary?.amountTotal || '0.00'}</span>
              </div>

              {/* Payment modes */}
              {(() => {
                const filteredData = shiftEndtDetail?.data?.cashCardSummary?.data?.filter(
                  (r) => !showUpArrow || r.type === 'cash'
                );
                if (!filteredData || filteredData.length === 0) {
                  return <div style={{ padding: '10px', color: '#888', textAlign: 'center' }}>No Record Found</div>;
                }
                return filteredData.map((r, i) => (
                  <div
                    key={i}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      margin: '7px 0',
                      paddingLeft: '7px',
                      border: 'solid 1px #bbb9b9',
                      backgroundColor: '#e1dfdf',
                      borderRadius: '5px',
                      color: 'black',
                      padding: r.type !== 'cash' || shiftStatus?.isDenomination === false ? '5px' : '0 0 0 6px',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <span>{r.name}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                      <span>{Number(r.totalAmount).toFixed(2)}</span>
                      {r.type === 'cash' && shiftStatus?.isDenomination === true && (
                        <span
                          onClick={toggleUpArrow}
                          style={{ padding: '6px 4px', backgroundColor: '#f9c9c9', cursor: 'pointer' }}
                        >
                          {showUpArrow ? (
                            <ArrowUpwardIcon style={{ color: 'red', fontSize: '18px' }} />
                          ) : (
                            <ArrowDownwardIcon style={{ color: 'red', fontSize: '18px' }} />
                          )}
                        </span>
                      )}
                    </div>
                  </div>
                ));
              })()}

              {shiftStatus?.isDenomination === true && (
                <textarea
                  placeholder="description for denomination.."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  style={{
                    width: '100%',
                    minHeight: '60px',
                    padding: '8px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    resize: 'vertical',
                    backgroundColor: 'rgb(245 241 241)',
                  }}
                />
              )}

              {!showUpArrow && (
                <>
                  <h4 style={{ margin: '21px 0px 7px 0px', justifySelf: 'start', fontWeight: '400' }}>Order Total</h4>
                  {(() => {
                    const posOrders = shiftEndtDetail?.data?.posOrderSummary;
                    if (!posOrders || posOrders.length === 0) {
                      return <div style={{ padding: '10px', color: '#888', textAlign: 'center' }}>No Record Found</div>;
                    }
                    return posOrders.map((r, i) => {
                      let backgroundColor = '';
                      let color = '';
                      let padding = '';
                      if (r.ordType === 'DELIVERY') {
                        backgroundColor = 'rgb(194 199 253)';
                        color = 'rgb(13 38 113)';
                        padding = '4px 21px 4px 12px';
                      } else if (r.ordType === 'DINEIN') {
                        backgroundColor = '#f5cdcd';
                        color = '#c50909';
                        padding = '4px 30px 4px 12px';
                      } else if (r.ordType === 'TAKEAWAY') {
                        backgroundColor = 'rgb(200 231 204)';
                        color = 'rgb(47 155 61)';
                        padding = '4px 3px 4px 8px';
                      }
                      return (
                        <div
                          key={i}
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            color,
                            paddingInline: '0 10px',
                            border: 'solid 1px #bbb9b9',
                            backgroundColor: ' rgb(225, 223, 223)',
                            borderRadius: '5px',
                            margin: '9px 0',
                          }}
                        >
                          <span
                            style={{
                              borderRadius: '4px 0 0 4px',
                              padding,
                              backgroundColor,
                              color,
                              border: 'solid 1px #cfc9c9',
                            }}
                          >
                            {r.ordType}
                          </span>
                          <span style={{ padding: '4px', color: 'black' }}>{r.amtTotal}</span>
                        </div>
                      );
                    });
                  })()}
                </>
              )}

              {showUpArrow && shiftStatus?.isDenomination === true && (
                <div>
                  {entries.map((entry, index) => (
                    <div
                      key={index}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '8px',
                        margin: '9px 0',
                        border: '1px solid #bbb9b9',
                        borderRadius: '5px',
                        backgroundColor: '#e1dfdf',
                      }}
                    >
                      <input
                        type="number"
                        placeholder="currency"
                        value={entry.currency}
                        onChange={(e) => handleChange(index, 'currency', e.target.value)}
                        style={{ padding: '6px', width: '35%', border: '1px solid #ccc', borderRadius: '4px', backgroundColor: '#fff' }}
                      />
                      <input
                        type="number"
                        placeholder="Count"
                        value={entry.count}
                        onChange={(e) => handleChange(index, 'count', e.target.value)}
                        style={{ padding: '6px', width: '35%', border: '1px solid #ccc', borderRadius: '4px', backgroundColor: '#fff' }}
                      />
                      <button
                        onClick={() => deleteEntry(index)}
                        style={{
                          marginLeft: '8px',
                          backgroundColor: '#ff4d4d',
                          border: 'none',
                          color: 'white',
                          padding: '5px 8px',
                          borderRadius: '4px',
                          cursor: 'pointer',
                        }}
                      >
                        X
                      </button>
                    </div>
                  ))}
                  <div style={{ textAlign: 'center' }}>
                    <button
                      onClick={addEntry}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: '#14008C',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '5px',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                      }}
                    >
                      + Row
                    </button>
                  </div>
                </div>
              )}

              {/* Submit */}
              <div style={{ textAlign: 'center', marginTop: '20px' }}>
                {!showUpArrow && (
                  <button
                    className="btn btn-primary"
                    style={{ backgroundColor: '#14008C', width: '50%', color: 'white', fontWeight: 'bold' }}
                    onClick={() => {
                      setShiftEndPasswordDialog(true);
                      setDenominationDialog(false);
                    }}
                  >
                    Submit
                  </button>
                )}
                {showUpArrow && (
                  <button
                    className="btn btn-primary"
                    style={{ backgroundColor: '#14008C', width: '50%', color: 'white', fontWeight: 'bold' }}
                    onClick={handleAddSubmit}
                  >
                    ADD
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* check-in password popup (re-check-in after checkout) */}
      {showCheckinPopup && (
        <div className="popup-overlay">
          <div className="popup-box" style={{ width: '295px' }}>
            <div
              className="popup-box-head"
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                height: '5vh',
                paddingInline: '6px',
                backgroundColor: '#14008C',
              }}
            >
              <img
                src={photo}
                alt=""
                style={{ borderRadius: '30px', height: '4vh', alignSelf: 'center' }}
              />
              <p style={{ color: 'white', margin: 0 }}>{userInfo?.userName}</p>
              <IconButton onClick={() => setShowCheckinPopup(false)}>
                <HighlightOffOutlinedIcon sx={{ color: 'white', fontSize: '28px' }} />
              </IconButton>
            </div>
            <div style={{ paddingBottom: '11px' }}>
              <div style={{ paddingInline: '10px' }}>
                <p style={{ justifySelf: 'start', margin: '17px 7px 3px 3px' }}>Password</p>
                <input
                  type="password"
                  style={{
                    backgroundColor: '#ede9e9',
                    width: '100%',
                    padding: '8px',
                    marginTop: '10px',
                    borderRadius: '4px',
                    border: '1px solid #ccc',
                  }}
                  value={checkinPassword}
                  onChange={(e) => setCheckinPassword(e.target.value)}
                  onKeyDown={(e) => (e.key === 'Enter' ? verifyCheckinPass(checkinPassword) : undefined)}
                />
              </div>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', paddingTop: '10px' }}>
                <button
                  className="btn btn-success"
                  style={{
                    fontWeight: 'bold',
                    color: 'black',
                    backgroundColor: 'white',
                    width: '78px',
                    border: 'solid 1px black',
                  }}
                  onClick={() => setShowCheckinPopup(false)}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  style={{ color: 'white', backgroundColor: '#14008C', width: '78px' }}
                  onClick={() => verifyCheckinPass(checkinPassword)}
                >
                  Check In
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* checkout step 1 - confirm employee */}
      {showCheckOutPopup && (
        <div className="popup-overlay">
          <div className="popup-box" style={{ width: '295px' }}>
            <div
              className="popup-box-head"
              style={{ display: 'flex', justifyContent: 'flex-end', paddingInline: '3px' }}
            >
              <IconButton onClick={() => setShowCheckOutPopup(false)}>
                <HighlightOffOutlinedIcon sx={{ color: 'white', fontSize: '28px' }} />
              </IconButton>
            </div>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <Avatar sx={{ width: 56, height: 56, bgcolor: '#bdbdbd', marginTop: '-20px' }}>
                <PersonIcon sx={{ fontSize: 36 }} />
              </Avatar>
            </div>
            <div style={{ paddingBottom: '11px', textAlign: 'center' }}>
              <p style={{ color: 'gray' }}>Employee</p>
              <p>
                {selectedCheckoutEmployee?.staff_name ||
                  selectedCheckoutEmployee?.name ||
                  userInfo?.userName}
              </p>
              <button
                className="btn btn-primary"
                style={{ height: '33px', width: '37%' }}
                onClick={() => {
                  setShowSecondCheckOutPopup(true);
                  setShowCheckOutPopup(false);
                }}
              >
                Check Out
              </button>
            </div>
          </div>
        </div>
      )}

      {/* checkout step 2 - password */}
      {showSecondCheckOutPopup && (
        <div className="popup-overlay">
          <div className="popup-box" style={{ width: '295px' }}>
            <div
              className="popup-box-head"
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                height: '5vh',
                paddingInline: '6px',
                backgroundColor: '#14008C',
              }}
            >
              <img
                src={photo}
                alt=""
                style={{ borderRadius: '30px', height: '4vh', alignSelf: 'center' }}
              />
              <p style={{ color: 'white', margin: 0 }}>
                {selectedCheckoutEmployee?.staff_name ||
                  selectedCheckoutEmployee?.name ||
                  userInfo?.userName}
              </p>
              <IconButton onClick={() => setShowSecondCheckOutPopup(false)}>
                <HighlightOffOutlinedIcon sx={{ color: 'white', fontSize: '28px' }} />
              </IconButton>
            </div>
            <div style={{ paddingBottom: '11px' }}>
              <div style={{ paddingInline: '10px' }}>
                <p style={{ justifySelf: 'start', margin: '17px 7px 3px 3px' }}>Password</p>
                <input
                  type="password"
                  style={{
                    backgroundColor: '#ede9e9',
                    width: '100%',
                    padding: '8px',
                    marginTop: '10px',
                    borderRadius: '4px',
                    border: '1px solid #ccc',
                  }}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyDown={(e) => (e.key === 'Enter' ? VerifyCheckOutPass(password) : undefined)}
                />
              </div>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', paddingTop: '10px' }}>
                <button
                  className="btn btn-success"
                  style={{
                    fontWeight: 'bold',
                    color: 'black',
                    backgroundColor: 'white',
                    width: '78px',
                    border: 'solid 1px black',
                  }}
                  onClick={() => {
                    setShowSecondCheckOutPopup(false);
                    setShowCheckOutPopup(true);
                  }}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-secondary"
                  style={{ color: 'white', backgroundColor: 'red', width: '78px' }}
                  onClick={() => VerifyCheckOutPass(password)}
                >
                  Submit
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* checkout step 3 - denomination / summary + submit */}
      {showFinalCheckOutPopup && (
        <div className="popup-overlay">
          <div className="popup-box" style={{ width: '387px', borderRadius: '10px', overflow: 'hidden' }}>
            {/* Top bar */}
            <div
              style={{
                backgroundColor: '#14008C',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '10px',
                color: 'white',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <img src={photo} alt="" style={{ borderRadius: '50%', height: '35px', width: '35px' }} />
                <span style={{ fontWeight: 'bold' }}>
                  {selectedCheckoutEmployee?.staff_name ||
                    selectedCheckoutEmployee?.name ||
                    userInfo?.userName}
                </span>
              </div>
              <IconButton onClick={() => setShowFinalCheckOutPopup(false)} style={{ color: 'white' }}>
                <HighlightOffOutlinedIcon />
              </IconButton>
            </div>
            {/* Content */}
            <div style={{ padding: '15px', backgroundColor: '#fff' }}>
              <h4 style={{ margin: '1px 0px 15px 0px', justifySelf: 'start', fontWeight: '400' }}>Denomination</h4>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: '10px',
                  paddingInline: '9px',
                  borderRadius: '3px',
                  backgroundColor: 'rgb(163 160 160)',
                }}
              >
                <span>Total</span>
                <span>{checkOutPassDetail?.data?.cashCardSummary?.amountTotal || '0.00'}</span>
              </div>

              {/* Payment modes */}
              {(() => {
                const filteredData = checkOutPassDetail?.data?.cashCardSummary?.data?.filter(
                  (r) => !showUpArrow || r.type === 'cash'
                );
                if (!filteredData || filteredData.length === 0) {
                  return <div style={{ padding: '10px', color: '#888', textAlign: 'center' }}>No Record Found</div>;
                }
                return filteredData.map((r, i) => (
                  <div
                    key={i}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      margin: '7px 0',
                      paddingLeft: '7px',
                      border: 'solid 1px #bbb9b9',
                      backgroundColor: '#e1dfdf',
                      color: 'black',
                      borderRadius: '5px',
                      padding: r.type !== 'cash' || shiftStatus?.isDenomination === false ? '5px' : '0 0 0 6px',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <span>{r.name}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                      <span>{Number(r.totalAmount).toFixed(2)}</span>
                      {r.type === 'cash' && shiftStatus?.isDenomination === true && (
                        <span
                          onClick={toggleUpArrow}
                          style={{ padding: '6px 4px', backgroundColor: '#f9c9c9', cursor: 'pointer' }}
                        >
                          {showUpArrow ? (
                            <ArrowUpwardIcon style={{ color: 'red', fontSize: '18px' }} />
                          ) : (
                            <ArrowDownwardIcon style={{ color: 'red', fontSize: '18px' }} />
                          )}
                        </span>
                      )}
                    </div>
                  </div>
                ));
              })()}

              {shiftStatus?.isDenomination === true && (
                <textarea
                  placeholder="description for denomination.."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  style={{
                    width: '100%',
                    minHeight: '60px',
                    padding: '8px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    resize: 'vertical',
                    backgroundColor: 'rgb(245 241 241)',
                  }}
                />
              )}

              {!showUpArrow && (
                <>
                  <h4 style={{ margin: '21px 0px 7px 0px', justifySelf: 'start', fontWeight: '400' }}>Order Total</h4>
                  {(() => {
                    const posOrders = checkOutPassDetail?.data?.posOrderSummary;
                    if (!posOrders || posOrders.length === 0) {
                      return <div style={{ padding: '10px', color: '#888', textAlign: 'center' }}>No Record Found</div>;
                    }
                    return posOrders.map((r, i) => {
                      let backgroundColor = '';
                      let color = '';
                      let padding = '';
                      if (r.ordType === 'DELIVERY') {
                        backgroundColor = 'rgb(194 199 253)';
                        color = 'rgb(13 38 113)';
                        padding = '4px 21px 4px 12px';
                      } else if (r.ordType === 'DINEIN') {
                        backgroundColor = '#f5cdcd';
                        color = '#c50909';
                        padding = '4px 30px 4px 12px';
                      } else if (r.ordType === 'TAKEAWAY') {
                        backgroundColor = 'rgb(200 231 204)';
                        color = 'rgb(47 155 61)';
                        padding = '4px 3px 4px 8px';
                      }
                      return (
                        <div
                          key={i}
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            color,
                            paddingInline: '0 10px',
                            border: 'solid 1px #bbb9b9',
                            backgroundColor: ' rgb(225, 223, 223)',
                            borderRadius: '5px',
                            margin: '9px 0',
                          }}
                        >
                          <span
                            style={{
                              borderRadius: '4px 0 0 4px',
                              padding,
                              backgroundColor,
                              color,
                              border: 'solid 1px #cfc9c9',
                            }}
                          >
                            {r.ordType}
                          </span>
                          <span style={{ padding: '4px', color: 'black' }}>{r.amtTotal}</span>
                        </div>
                      );
                    });
                  })()}
                </>
              )}

              {showUpArrow && shiftStatus?.isDenomination === true && (
                <div>
                  {entries.map((entry, index) => (
                    <div
                      key={index}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '8px',
                        margin: '9px 0',
                        border: '1px solid #bbb9b9',
                        borderRadius: '5px',
                        backgroundColor: '#e1dfdf',
                      }}
                    >
                      <input
                        type="number"
                        placeholder="currency"
                        value={entry.currency}
                        onChange={(e) => handleChange(index, 'currency', e.target.value)}
                        style={{ padding: '6px', width: '35%', border: '1px solid #ccc', borderRadius: '4px', backgroundColor: '#fff' }}
                      />
                      <input
                        type="number"
                        placeholder="Count"
                        value={entry.count}
                        onChange={(e) => handleChange(index, 'count', e.target.value)}
                        style={{ padding: '6px', width: '35%', border: '1px solid #ccc', borderRadius: '4px', backgroundColor: '#fff' }}
                      />
                      <button
                        onClick={() => deleteEntry(index)}
                        style={{
                          marginLeft: '8px',
                          backgroundColor: '#ff4d4d',
                          border: 'none',
                          color: 'white',
                          padding: '5px 8px',
                          borderRadius: '4px',
                          cursor: 'pointer',
                        }}
                      >
                        X
                      </button>
                    </div>
                  ))}
                  <div style={{ textAlign: 'center' }}>
                    <button
                      onClick={addEntry}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: '#14008C',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '5px',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                      }}
                    >
                      + Row
                    </button>
                  </div>
                </div>
              )}

              {/* Submit */}
              <div style={{ textAlign: 'center', marginTop: '20px' }}>
                {!showUpArrow && (
                  <button
                    className="btn btn-primary"
                    style={{ backgroundColor: '#14008C', width: '50%', color: 'white', fontWeight: 'bold' }}
                    onClick={handleSubmitheckout}
                  >
                    Submit
                  </button>
                )}
                {showUpArrow && (
                  <button
                    className="btn btn-primary"
                    style={{ backgroundColor: '#14008C', width: '50%', color: 'white', fontWeight: 'bold' }}
                    onClick={handleAddSubmit}
                  >
                    ADD
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ShiftControls;
